import React, { useState, useEffect } from 'react';
import { Shield, Copy, Check, Key, Smartphone, Lock, ArrowLeft, QrCode, ShieldCheck, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import useToast from '../../hooks/useToast';

export default function TwoFactor() {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState('status');
  const [secret, setSecret] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const toast = useToast();

  useEffect(() => { checkStatus(); }, []);

  const checkStatus = async () => {
    try {
      const res = await api.get('/api/auth/2fa/status');
      setStatus(res.data.enabled);
    } catch { toast.error('Failed to check 2FA status'); }
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/auth/2fa/setup');
      if (res.data.enabled) { setStatus(true); toast.info('2FA is already enabled'); }
      else { setSecret(res.data.secret); setQrUrl(res.data.qr_url); setManualKey(res.data.manual_key); setStep('setup'); }
    } catch { toast.error('Failed to setup 2FA'); }
    finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Enter a valid 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/auth/2fa/enable', { code });
      setBackupCodes(res.data.backup_codes || []);
      setStatus(true); setStep('success');
      toast.success('2FA enabled successfully!', { duration: 5000 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid verification code'); }
    finally { setLoading(false); }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (disableCode.length !== 6) { toast.error('Enter a valid 6-digit code'); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/2fa/disable', { code: disableCode });
      setStatus(false); setShowDisable(false); setDisableCode(''); setStep('status');
      toast.success('2FA has been disabled', { duration: 4000 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); }
    finally { setLoading(false); }
  };

  const handleCopy = async (text, index) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
      toast.success('Code copied!', { duration: 2000 });
    } else {
      toast.error('Failed to copy. Please copy manually.');
    }
  };

  const copyAllCodes = () => {
    const text = backupCodes.join('\n');
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          toast.success('All backup codes copied!', { duration: 2000 });
        }).catch(() => fallbackCopyAll(text));
      } else {
        fallbackCopyAll(text);
      }
    } catch {
      fallbackCopyAll(text);
    }
  };
  
  const fallbackCopyAll = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      toast.success('All backup codes copied!', { duration: 2000 });
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
    document.body.removeChild(textarea);
  };

  if (status === null) {
    return (
      <div className="page-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="page-content" style={{ maxWidth: 'clamp(320px, 100%, 640px)', margin: '0 auto' }}>
      
      {/* ═══════════════ HEADER ═══════════════ */}
      <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: 0, marginBottom: 'var(--space-xl)' }}>
        <ArrowLeft size={16} /> Back to Settings
      </button>

      {/* ═══════════════ STATUS: ENABLED ═══════════════ */}
      {status && step === 'status' && (
        <div style={{ animation: 'fadeSlideIn 0.4s ease' }}>
          <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--success)', borderLeft: '3px solid var(--success)',
            borderRadius: 'var(--radius-xl)', padding: 'var(--space-2xl)', boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--success-bg)', border: '2px solid var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShieldCheck size={24} color="var(--success)" />
              </div>
              <div>
                <h3 style={{ color: 'var(--success)', fontSize: 17, fontWeight: 700, margin: '0 0 2px' }}>2FA is Active</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>Your account requires a verification code from your authenticator app when logging in.</p>
              </div>
            </div>
          </div>

          {!showDisable ? (
            <button onClick={() => setShowDisable(true)} style={{
              padding: '10px 20px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)',
              background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.2s ease',
            }}>
              Disable Two-Factor Authentication
            </button>
          ) : (
            <form onSubmit={handleDisable} style={{
              background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
              borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)',
              animation: 'fadeSlideIn 0.3s ease',
            }}>
              <h4 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>Disable 2FA</h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 14 }}>Enter a verification code from your authenticator app to confirm.</p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="text" value={disableCode} onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} style={{
                  flex: 1, minWidth: 140, padding: '12px 14px', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-primary)', background: 'var(--input-bg)',
                  color: 'var(--text-primary)', fontSize: 20, textAlign: 'center', letterSpacing: 8,
                  outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box',
                }} autoFocus />
                <button type="submit" disabled={loading || disableCode.length !== 6} style={{
                  padding: '12px 20px', borderRadius: 'var(--radius-md)', border: 'none',
                  background: disableCode.length === 6 ? 'var(--error)' : 'var(--bg-tertiary)',
                  color: disableCode.length === 6 ? 'white' : 'var(--text-tertiary)',
                  cursor: disableCode.length === 6 ? 'pointer' : 'not-allowed',
                  fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                }}>{loading ? 'Disabling...' : 'Confirm Disable'}</button>
                <button type="button" onClick={() => { setShowDisable(false); setDisableCode(''); }} style={{
                  padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)',
                  background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13,
                }}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ═══════════════ STATUS: DISABLED ═══════════════ */}
      {!status && step === 'status' && (
        <div style={{ animation: 'fadeSlideIn 0.4s ease' }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--accent-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-xl)' }}>
              <Shield size={32} color="var(--accent-primary)" />
            </div>
            <h1 style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>Two-Factor Authentication</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>
              Add an extra layer of security to your account. Once enabled, you'll need both your password and a code from your phone to log in.
            </p>
          </div>

          {/* Feature cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-2xl)' }}>
            {[
              { icon: <Smartphone size={18} color="var(--accent-primary)" />, title: 'Authenticator App', desc: 'Works with Google, Authy, Microsoft Authenticator' },
              { icon: <Key size={18} color="var(--accent-primary)" />, title: '8 Backup Codes', desc: 'One-time use codes for emergencies' },
              { icon: <Shield size={18} color="var(--accent-primary)" />, title: '2-Minute Setup', desc: 'Scan QR code, enter one code, done' },
            ].map((item, i) => (
              <div key={i} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-lg)', textAlign: 'center', animation: `fadeSlideIn 0.4s ease ${0.1 * i}s both` }}>
                <div style={{ marginBottom: 8 }}>{item.icon}</div>
                <p style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, margin: '0 0 2px' }}>{item.title}</p>
                <p style={{ color: 'var(--text-tertiary)', fontSize: 11, margin: 0, lineHeight: 1.4 }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <button onClick={handleSetup} disabled={loading} className="auth-button auth-button-primary" style={{ width: '100%', height: 50, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Lock size={18} /> Enable Two-Factor Authentication
          </button>
        </div>
      )}

      {/* ═══════════════ SETUP STEP ═══════════════ */}
      {step === 'setup' && (
        <div style={{ animation: 'slideInRight 0.35s ease' }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
            <h2 style={{ color: 'var(--text-primary)', fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>Set Up Your Authenticator</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>Scan the QR code or enter the key manually</p>
          </div>

          {/* Steps */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 'var(--space-xl)' }}>
            {['Open your authenticator app', 'Scan this QR code', 'Enter the 6-digit code below'].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-secondary)', fontSize: 13 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent-glow)', color: 'var(--accent-primary)', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
                {s}
              </div>
            ))}
          </div>

          {/* QR Code */}
          <div style={{ background: '#ffffff', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', textAlign: 'center', marginBottom: 'var(--space-lg)', border: '1px solid var(--border-primary)' }}>
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`} alt="QR Code" style={{ width: 180, height: 180, display: 'block', margin: '0 auto' }} />
            <p style={{ color: '#888', fontSize: 11, marginTop: 10 }}>Scan with your authenticator app</p>
          </div>

          {/* Manual key */}
          <div style={{ marginBottom: 'var(--space-xl)' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 6 }}>Can't scan? Use this key:</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ flex: 1, padding: '10px 14px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all' }}>{manualKey}</code>
              <button onClick={async () => {
                const success = await copyToClipboard(manualKey);
                success ? toast.success('Key copied!') : toast.error('Failed to copy');
              }} style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
                <Copy size={14} />
              </button>
            </div>
          </div>

          {/* Verification */}
          <form onSubmit={handleVerify} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)' }}>
            <h4 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>Verify Setup</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 14 }}>Enter the 6-digit code from your authenticator app.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
              {[0, 1, 2, 3, 4, 5].map(i => (
                <div key={i} style={{
                  width: 44, height: 52, borderRadius: 'var(--radius-md)',
                  border: code.length === i ? '2px solid var(--accent-primary)' : '1px solid var(--border-primary)',
                  background: 'var(--input-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, fontWeight: 700, fontFamily: 'monospace', color: 'var(--text-primary)',
                  boxShadow: code.length === i ? '0 0 0 3px var(--accent-glow)' : 'none',
                  transition: 'all 0.15s ease',
                }}>
                  {code[i] || ''}
                </div>
              ))}
            </div>
            <input type="text" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} autoFocus style={{
              position: 'absolute', opacity: 0, width: 1, height: 1,
            }} />
            <button type="submit" disabled={loading || code.length !== 6} className="auth-button auth-button-primary" style={{
              width: '100%', height: 48, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              opacity: code.length === 6 ? 1 : 0.5,
            }}>
              {loading ? 'Verifying...' : <><ShieldCheck size={16} /> Verify & Enable</>}
            </button>
          </form>

          <button onClick={() => setStep('status')} style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>
            ← Back
          </button>
        </div>
      )}

      {/* ═══════════════ SUCCESS STEP ═══════════════ */}
      {step === 'success' && (
        <div style={{ animation: 'scaleIn 0.4s ease' }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--success-bg)', border: '2px solid var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-lg)', animation: 'notificationBounce 0.5s ease' }}>
              <Check size={32} color="var(--success)" />
            </div>
            <h2 style={{ color: 'var(--text-primary)', fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>2FA Enabled!</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 380, margin: '0 auto', lineHeight: 1.5 }}>
              Your account is now protected. Save these backup codes,  you'll need them if you lose access to your authenticator app.
            </p>
          </div>

          {/* Backup codes */}
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--warning)', borderLeft: '3px solid var(--warning)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h4 style={{ color: 'var(--warning)', fontSize: 14, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}><Key size={16} /> Backup Codes</h4>
              <button onClick={copyAllCodes} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--warning)', background: 'transparent', color: 'var(--warning)', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><Copy size={12} /> Copy All</button>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 14 }}>Each code can only be used once. Store them somewhere safe.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {backupCodes.map((bc, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', fontFamily: 'monospace', fontSize: 13, color: 'var(--text-primary)' }}>
                  <span>{bc.slice(0, 4)}-{bc.slice(4)}</span>
                  <button onClick={() => handleCopy(bc, i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2 }}>
                    {copiedIndex === i ? <Check size={14} color="var(--success)" /> : <Copy size={14} />}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button onClick={() => setStep('status')} className="auth-button auth-button-primary" style={{ width: '100%', height: 48, fontSize: 14 }}>
            <ShieldCheck size={16} style={{ marginRight: 6 }} /> Go to Security Settings
          </button>
        </div>
      )}
    </div>
  );
}
